import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '..'
import reportsService from '../../services/reportsService'
import reportsSelectors from './reportsSelectors'
import questionsAnswersUtils from '../../utils/questionsAnswersUtils'
import actions from './actions'
import {
  ICasetypeQuestions,
  IReportsState,
  IReport,
  triageLevelStates,
  IReportDTO,
  MainObject,
  SubAnswer
} from './types'
import { INewVictim } from '../questions/types'
import { diff } from 'deep-diff'
import { getUserAccessOnReport } from '../auth/authSlice'
import { userRoles, usersReportAccess } from '../auth/userTypes'
import usersService from '../../services/usersService'
import { usersReportRoles } from '../users/types'
import { findAnswer } from '../questions/questionsSelector'
import { error } from '../../utils/alertUtils';
import { v4 as uuidv4 } from 'uuid'


const initialState: IReportsState = {
  requiresTriage: [],
  immediateAttention: [],
  additionalInformationRequired: [],
  addressedAndResolved: [],
  addressedAndResolvedLoading: false,
  assignedToMeForTriage: [],
  acceptedReports: [],
  openedCase: undefined,
  reportAnswers: [],
  caseTypeQuestions: [],
  loading: false,
  requiresTriageLoading: false,
  userCasesLoading: false,
  caseIsOpening: false,
  caseTypeQuestionAnswers: [],
  defaultCaseTypeQuestionAnswers: [],
  answersToUpdate: [],
  victimAnswers: [],
  victims: [],
  numberOfChanges: 0,
}

const calculateDifferences = (defaultArray: MainObject[], newArray: MainObject[]) => {
  const differences = diff(defaultArray, newArray)
  return differences?.length ?? 0
}

export const findItemByUuid = (items: any[], uuid: any): MainObject | SubAnswer | null => {
  if (!items) {
    return null
  }
  for (const item of items) {
    if (item.uuid === uuid) {
      return item
    }
    const subitem = findItemByUuid(item?.subanswers, uuid)
    if (subitem) {
      return subitem
    }
  }
  return null
}


function mergeArrays(arrayA: IReport[], arrayB: IReport[]) {
  const existingIds = new Set(arrayA.map(obj => obj.id))
  const uniqueObjects = arrayB.filter(obj => !existingIds.has(obj.id))
  return arrayA.concat(uniqueObjects)
}

export const getRequiresTriage = createAsyncThunk(
    actions.GET_REQUIRES_TRIAGE,
    async (_, { rejectWithValue }) => {
      try {
        const { data } = await reportsService.getAllRequiresTriage()
        const categorizedReports: { [key: string]: IReport[] } = {
          requiresTriage: [],
          [triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED]: [],
          [triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED]: [],
        }

        data?.forEach((report: IReport) => {
          if (!report.triage_level) {
            categorizedReports['requiresTriage']?.push(report)
          }
          categorizedReports[report?.triage_level]?.push(report)
        })
        return categorizedReports
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong while fetching inbox data')
          return rejectWithValue('Something went wrong while fetching inbox data')
        }
      }
    },
)

export const getUsersReports = createAsyncThunk(
    actions.GET_USERS_REPORTS,
    async (_, { rejectWithValue, getState }) => {
      try {
        const { data } = await reportsService.getUsersReports()
        const usersReports = data?.filter(
          (item: IReportDTO, index: number, self: IReportDTO[]) =>
            self.findIndex((r: IReportDTO) => r.report.id === item.report.id) === index,
        )


        const categorizedReports: { [key: string]: IReport[] } = {
          [triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED]: [],
          [triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED]: [],
          [triageLevelStates.ADDRESSED_AND_RESOLVED]: [],
          [triageLevelStates.TRANSFERED_FOR_ASSIGNMENT]: [],
          [triageLevelStates.ACCEPTED_AND_ASSIGNED]: [],
        }

        usersReports?.forEach((item: IReportDTO) => {
          const triageLevel = item?.report?.triage_level
          if (triageLevel) {
            categorizedReports[triageLevel]?.push(item.report)
          }else{
            categorizedReports[triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED]?.push(item.report)
          }
        })
        return categorizedReports
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong while fetching inbox data')
          return rejectWithValue('Something went wrong while fetching inbox data')
        }
      }
    },
)

export const getAdminReports = createAsyncThunk(
    actions.GET_ADMIN_REPORTS,
    async (_, { rejectWithValue, getState }) => {
      try {
        // eslint-disable-next-line
        const { data } = await reportsService.getAdminReports()
        const usersReports = data?.filter(
          (item: IReportDTO, index: number, self: IReportDTO[]) =>
            self.findIndex((r: IReportDTO) => r.report.id === item.report.id) === index,
        )

        // eslint-disable-next-line
        const categorizedReports: { [key: string]: IReport[] } = {
          [triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED]: [],
          [triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED]: [],
          [triageLevelStates.ADDRESSED_AND_RESOLVED]: [],
          [triageLevelStates.TRANSFERED_FOR_ASSIGNMENT]: [],
          [triageLevelStates.ACCEPTED_AND_ASSIGNED]: [],
        }

        usersReports?.forEach((item: IReportDTO) => {
          const triageLevel = item?.report?.triage_level
          if (triageLevel) {
            categorizedReports[triageLevel]?.push(item.report)
          }
        })
        return categorizedReports
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong while fetching inbox data')
          return rejectWithValue('Something went wrong while fetching inbox data')
        }
      }
    },
)

export const openCase = createAsyncThunk(
    actions.OPEN_CASE,
    async (payload: { caseId: number; caseStatus?: string }, { rejectWithValue, dispatch }) => {
      try {
        
        const res = await reportsService.getCaseById(payload.caseId)
        const resolved = await reportsService.getResolvedDataFromReport(payload.caseId);

        if(resolved && resolved.length > 0){
          const resolvedData = resolved.find((item: any) => item.report.id === payload.caseId);
           if(resolvedData){
            res.resolvedData = resolvedData;
           }
        }
       
        dispatch(
            getUserAccessOnReport({
              reportId: payload.caseId,
              caseStatus: payload.caseStatus,
            }),
        )

        return res as unknown as IReport
      } catch (err) {
        if (err instanceof Error) {
          error('Error while opening case')
          return rejectWithValue('Something went wrong while opening case')
        }
      }
    },
)

export const getReportAnswers = createAsyncThunk(
    actions.GET_REPORT_ANSWERS,
    async (payload: { reportId: number }, { rejectWithValue }) => {
      try {
        const { data } = await reportsService.getReportAnswers(payload.reportId)
        return data
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong with fetching report answers')
          return rejectWithValue('Something went wrong with fetching report answers')
        }
      }
    },
)

export const getCaseTypeQuestions = createAsyncThunk(
    actions.GET_CASE_TYPE_QUESTIONS,
    async (payload: { caseType: string }, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState
        const alreadyExists = state?.reports?.caseTypeQuestions.find(
            (item: ICasetypeQuestions) => item.caseType === payload.caseType,
        )
        if (alreadyExists) {
          return undefined
        }
        const { data } = await reportsService.getCaseTypeQuestions({
          caseType: payload.caseType,
          caseId: state.reports.openedCase?.uuid ? state.reports.openedCase?.uuid : '',
        })

        const caseTypeQuestions = {
          caseType: payload.caseType,
          questions: data,
        } as ICasetypeQuestions

        return caseTypeQuestions
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong with case type questions')
          return rejectWithValue('Something went wrong with case type questions')
        }
      }
    },
)

export const getQuestionsForArrayOfTypes = createAsyncThunk(
    actions.GET_CASE_TYPES_QUESTIONS,
    async (payload: { caseTypes: string[] }, { rejectWithValue, getState, dispatch }) => {
      try {
        const state = getState() as RootState
        const alreadyExists = state?.reports?.caseTypeQuestions.find((item: ICasetypeQuestions) =>
            payload.caseTypes.some(newCaseTypes => item?.caseType === newCaseTypes),
        )
        if (alreadyExists) {
          return undefined
        }

        const promises = payload.caseTypes?.map(async (caseType: string) => {
          return await reportsService
              .getCaseTypeQuestions({
                caseType,
                caseId: state.reports.openedCase?.uuid ? state.reports.openedCase?.uuid : '',
              })
              .then(response => {
                return {
                  caseType,
                  questions: response.data,
                } as ICasetypeQuestions
              })
        })
        const caseTypeQuestions = await Promise.all(promises).then(res => {
          return res?.map(response => response)
        })
        return caseTypeQuestions
      } catch (err) {
        if (err instanceof Error) {
          error('Error while fetching case type questions')
          return rejectWithValue('Something went wrong with case type questions')
        }
      }
    },
)

export const updateReportTriage = createAsyncThunk(
    actions.UPDATE_TRIAGE_LEVEL,
    async (
        payload: { type: string; dataBody: any; selectedReportId: number },
        { rejectWithValue, getState },
    ) => {
      const state = getState() as RootState
      const selectedReport = state.reports.openedCase


      try {
        if (
            payload?.type === triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED ||
            payload?.type === triageLevelStates.TRANSFERED_FOR_ASSIGNMENT
        ) {
          const res = await reportsService.postTriageLevel({
            selectedReportId: payload.selectedReportId,
            dataBody: payload.dataBody,
            triage_level: payload.type,
            actionType:
                selectedReport?.triage_level === triageLevelStates.TRANSFERED_FOR_ASSIGNMENT
                    ? 'reassign'
                    : 'assign',
          })
          return res
        }

        if (
            payload?.type === triageLevelStates.ADDRESSED_AND_RESOLVED ||
            payload?.type === triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED
        ) {
          const res = await reportsService.patchReport({
            selectedReportId: payload.selectedReportId,
            dataBody: payload.dataBody,
          })
          return res
        }

        if (payload?.type === triageLevelStates.ACCEPTED_AND_ASSIGNED) {
          // temporary solution
          // for new reports if user is DESIGNATED_RECEIVER and is trying to accept it that is not covered on Backend
          if (state.auth?.userRoles?.find(role => role === userRoles.DESIGNATED_RECEIVER)) {
            if (!selectedReport?.triage_level) {
              //create new report assign for designated and after patch report triage_level
              try {
                const responseCreateReportAssign = await usersService.postUserToTeam({
                  investigator: state.auth?.userProfileId as number,
                  report: selectedReport?.id as number,
                  access: usersReportAccess.CAN_EDIT,
                  role: usersReportRoles.LEAD_INVESTIGATOR,
                  message: `Added to team on report: ${selectedReport?.uuid}`,
                })

                const patchReportResponse = await reportsService.patchReport({
                  selectedReportId: selectedReport?.id as number,
                  dataBody: {
                    triage_level: triageLevelStates.ACCEPTED_AND_ASSIGNED,
                  },
                })

                return
              } catch (err) {
                // eslint-disable-next-line
                error('Something went wrong while creating report assign')
                // console.log('Something went wrong while creating report assign', err)
              }
            }
          }

          const res = await reportsService.postTriageLevel({
            selectedReportId: payload.selectedReportId,
            dataBody: payload.dataBody,
            actionType: 'accept',
          })

          return res
        }

        return {}
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong while updating Triage Level')
          return rejectWithValue('Something went wrong while updating Triage Level')
        }
      }
    },
)

export const getArchivedReports = createAsyncThunk(
    actions.GET_ARCHIVED_REPORTS,
    async (_, { rejectWithValue }) => {
      try {
        const { data } = await reportsService.getArchivedReports()
        return data
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong fetching Archived Reports')
          return rejectWithValue('Something went wrong fetching Archived Reports')
        }
      }
    },
)

export const getReportTeam = createAsyncThunk(
    actions.GET_REPORT_TEAM,
    async (_, { rejectWithValue, getState }) => {
      const state = getState() as RootState
      if (!state?.reports?.openedCase?.id) {
        return
      }
      try {
        const { data } = await reportsService.getTeamMembers(state?.reports?.openedCase?.id as number)
        return data.map((user: { investigator: any; access: any; role: any; id: number }) => {
          return {
            ...user?.investigator,
            access_role: user?.access,
            case_role: user?.role,
            reportAssingId: user?.id,
          }
        })
      } catch (err) {
        if (err instanceof Error) {
          error('Error fetching Team members')
          return rejectWithValue('Something went wrong while getting Team members')
        }
      }
    },
)

export const postAnswersToCaseTypeQuestions = createAsyncThunk(
    'postAnswersToCaseTypeQuestions',
    async (_, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState
        
        const answers = state?.reports?.caseTypeQuestionAnswers.filter(item => state?.reports?.answersToUpdate.includes(item.uuid))
        const { answersToUpdate = [], newAnswers = [] } = answers.reduce(
            (
                acc: { answersToUpdate: MainObject[]; newAnswers: MainObject[] },
                testAnswer: MainObject,
            ) => {
              const answer = { ...testAnswer }
              if (answer?.id) {
                acc.answersToUpdate.push(answer)
              } else {
                acc.newAnswers.push(answer)
              }
              return acc
            },
            { answersToUpdate: [], newAnswers: [] },
        )
        
        if (answersToUpdate?.length) {
          // const newArray = questionsAnswersUtils.removeQuestionPropertyFromObjects(answersToUpdate) // 
          const response = await reportsService.patchAnswers(
              state?.reports?.openedCase?.id as number,
              answersToUpdate,
          )
        }


        const unionObjectsByCaseType = (arr: MainObject[]) => {
          const union: { [key: string]: MainObject[] } = {}
          arr.forEach(obj => {
            const { caseType } = obj
            if (!union[caseType]) {
              union[caseType] = [obj]
            } else {
              union[caseType].push(obj)
            }
          })
          return union
        }

        
        // Call the function with the array of objects
        const union = unionObjectsByCaseType(newAnswers)
        const sortedAnswersByType = ''


        const promises = Object.entries(union).map(async ([caseType, objects]) => {
          return await reportsService.postAnswersToCaseTypeQuestions(
              state?.reports?.openedCase?.id as number,
              objects,
              caseType,
          )
        })
        Promise.all(promises)
            .then(res => {
              // eslint-disable-next-line
              // console.log('Success: ', res)
            })
            .catch(err => {
              // eslint-disable-next-line
              // console.log('Error:', err)
            })
        return {}
      } catch (err:any) {
        if (err instanceof Error) {
          error('Something went wrong while posting answers')
          return rejectWithValue('Something went wrong while posting answers')
        }
      }
    },
)

export const postCaseStatusMessage = createAsyncThunk(
    'postCaseStatusMessage',
    async (payload: { description: string }, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState
        const selectedReport = state.reports.openedCase
        const loggedUser = state?.auth?.userDetails?.userID

        const response = await reportsService.postCaseStatusMessage({
          description: payload?.description,
          report: selectedReport?.id,
          user: loggedUser,
        })

        return response.data
      } catch (err) {
        if (err instanceof Error) {
          error('Something went wrong while postCaseStatusMessage')
          return rejectWithValue('Something went wrong while postCaseStatusMessage')
        }
      }
    },
)

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    deselectCase: (state, action) => {
      state.openedCase = action.payload.value
    },
    removeSelectedCaseType: (state, { payload }) => {
      state.caseTypeQuestions = state.caseTypeQuestions.filter(
          (questions: ICasetypeQuestions) => questions.caseType !== payload.caseType,
      )
    },
    setInitialAnswers: (state, action) => {
      state.caseTypeQuestionAnswers = action.payload
    },
    updateSelectedReportCaseTypes: (state, { payload }) => {
      if (state.openedCase) {
        state.openedCase.types = payload
      }
    },
    clearCaseTypeQuestions: (state) => {
      state.caseTypeQuestions = [];
    },

    updateAnswerOption: (state, { payload }) => {
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if ( payload?.parentUUID ) {
        const parent = findItemByUuid(state.caseTypeQuestionAnswers, payload?.parentUUID)
        if (parent && item && !findItemByUuid(parent.subanswers, payload?.answerUUID)) {
          parent.subanswers.push(item as SubAnswer)
        }
      } 
      if (item) {
        item.options = payload?.answerText

        
        // if (item?.subanswers && item?.subanswers.length) {
        //   item.subanswers = item.subanswers.map((sub) => {
        //     return {...sub, answer: { ...sub.answer, text: payload?.answerText }}
        //   })
        // }

        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
        if (payload?.id && !item?.id) {
          item.id = payload?.id
        }
      }
      const numberOfChanges = calculateDifferences(
          state?.defaultCaseTypeQuestionAnswers,
          state?.caseTypeQuestionAnswers,
      )
      state.numberOfChanges = numberOfChanges
    },
    updateAnswerFiles: (state, { payload }) => {
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if (item) {
        if (item.answer.files) {
          item.answer.files.push(payload?.files)
        } else {
          item.answer.files = [payload.files]
        }
        const exists = state?.answersToUpdate.find(uuid => uuid === payload?.answerUUID)
        if (!exists) {
          state?.answersToUpdate.push(payload?.answerUUID)
        }
        if (payload?.id && !item?.id) {
          item.id = payload?.id
        }
      }
    },
    updateAnswerText: (state, { payload }) => {
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if ( payload?.parentUUID ) {
        const parent = findItemByUuid(state.caseTypeQuestionAnswers, payload?.parentUUID)
        if (parent && item && !findItemByUuid(parent.subanswers, payload?.answerUUID)) {
          parent.subanswers.push(item as SubAnswer)
        }
      } 
      if (item) {
        item.answer.text = payload?.answerText


        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
        if (payload?.id && !item?.id) {
          item.id = payload?.id
        }
      }
      const numberOfChanges = calculateDifferences(
          state?.defaultCaseTypeQuestionAnswers,
          state?.caseTypeQuestionAnswers,
      )
      state.numberOfChanges = numberOfChanges
    },
    setInitialValueToAnswer: (state, { payload }) => {
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if (item) {
        item.id = payload?.answer?.id

        if (payload?.answer?.files?.length > 0) {
          item.answer.files = payload?.answer?.files
        }
      

        if (payload?.answer?.text) {
          const answer = item?.answer ? item?.answer : {}
          item.answer = {...answer, text: payload?.answer?.text}
        }

        

        if (payload?.answer?.victim) {
          item.victim = payload?.answer?.victim
        }

        
        if (payload?.answer?.reporting_party) {
          item.reporting_party = payload?.answer?.reporting_party
        }

        if (payload?.answer?.answers) {
          item.options = payload?.answer?.answers.map((answer :any) => answer?.question_option?.id)
        }
        // // console.log(payload, JSON.parse(JSON.stringify(item)))
      }
    },
    setDefaultAnswersState: (state, action) => {
      state.defaultCaseTypeQuestionAnswers = state?.caseTypeQuestionAnswers
    },
    resetNumberOfChanges: (state, _) => {
      state.numberOfChanges = 0
    },
    updateVictimAnswerOption: (state, { payload }) => {
      const victim = state?.victimAnswers?.find(victim => victim.victimId === payload?.victimId)

      if (victim) {
        const item = findAnswer(victim?.answers, payload?.answerUUID)
        if (item) {
          item.options = payload?.answerText
        }
        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
      }
    },
    updateVictimAnswerText: (state, { payload }) => {
      const victim = state?.victimAnswers?.find(victim => victim.victimId === payload?.victimId)

      if (victim) {
        const item = findAnswer(victim?.answers, payload?.answerUUID)
        if (item) {
          item.answer.text = payload?.answerText
        }
        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
      }
    },
    setReporterValues: (state, { payload }) => {
      const { field, value, answerUUID} = payload
      const item = findItemByUuid(state.caseTypeQuestionAnswers, answerUUID)
      if (item) {
        let json: any = {}
        if (!item.answer.text.trim()) {
          json = {
            full_name: '',
            person_type: '',
            phone_number: '',
            email: '',
            work_site: '',
            city: '',
            work_country: '',
          }
        } else if (typeof item.answer.text === 'string') {
          
          json = JSON.parse(item.answer.text)
        }
        
        
        
        json[field as string] = value
        item.answer.text = JSON.stringify(json)
        
        state?.answersToUpdate.push(answerUUID)
      }
    },
    addNewVictimForm: (state, { payload }) => {
      
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if (item) {
        // The server sends a subanswer with an empty victim object
        if (!item.subanswers[0].victim) {
          // First start it
          item.subanswers[0].victim = {
            full_name: '',
            phone: '',
            email: '',
            work_site: '',
            city: '',
            state: '',
            work_country: '',
            person_type: 'UNKNOWN',
          }
        } else {

          item.subanswers.push({
            uuid: uuidv4(),
            answer: {
              question: 36,
              text: '',
            },
            victim: {
              full_name: '',
              phone: '',
              email: '',
              work_site: '',
              city: '',
              state: '',
              work_country: '',
              person_type: 'UNKNOWN',
            } 
          } as SubAnswer)	
        }
        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
      }
      
    },
    removeVictimForm: (state, { payload }) => {
      const item = findItemByUuid(state.caseTypeQuestionAnswers, payload?.answerUUID)
      if (item) {
        item.subanswers = item.subanswers.filter(
          sub => sub.uuid !== payload?.victimId,
        )
        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
      }
    },
    setVictimValues: (state, { payload }) => {
      const { field, value, victimId, answerUUID } = payload
      
      const item = findItemByUuid(state.caseTypeQuestionAnswers, answerUUID)
      
      if (item) {
        item.subanswers = item.subanswers.map((sub) => {
          if (sub.uuid == victimId) {
            sub.victim = { ...sub.victim, [field]: value }
          }
          return sub
        })
        state?.answersToUpdate.push(payload?.parentUUID || payload?.answerUUID)
      }
    }
  },
  extraReducers: builder => {
        builder.addCase(getRequiresTriage.fulfilled, (state, { payload }) => {
          const data = { ...payload }
          state.requiresTriageLoading = false
          state.requiresTriage = mergeArrays(state.requiresTriage, data['requiresTriage'])
          state.additionalInformationRequired = mergeArrays(
              state.additionalInformationRequired,
              data[triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED],
          )
          state.immediateAttention = mergeArrays(
              state.immediateAttention,
              data[triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED],
          )
        }), 
        builder.addCase(getRequiresTriage.pending, state => {
          state.requiresTriage = []
          state.additionalInformationRequired = []
          state.immediateAttention = []
          state.requiresTriageLoading = true
        }),
        builder.addCase(getRequiresTriage.rejected, state => {
          state.requiresTriageLoading = false
        }),
        builder.addCase(getUsersReports.fulfilled, (state, { payload }) => {
          // console.log('getUsersReports.fulfilled')
          const data = { ...payload }
          state.userCasesLoading = false
          state.assignedToMeForTriage = data[triageLevelStates.TRANSFERED_FOR_ASSIGNMENT]
          state.immediateAttention = mergeArrays(
              state.immediateAttention,
              data[triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED] || [])
          state.additionalInformationRequired = mergeArrays(
              state.additionalInformationRequired,
              data[triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED] || [])
          state.addressedAndResolved = data[triageLevelStates.ADDRESSED_AND_RESOLVED]
          state.acceptedReports = data[triageLevelStates.ACCEPTED_AND_ASSIGNED]
        }),
        builder.addCase(getUsersReports.pending, state => {
          // console.log('getUsersReports.pending')
          state.userCasesLoading = true
          state.assignedToMeForTriage = []
          state.immediateAttention = []
          state.additionalInformationRequired = []
          state.addressedAndResolved = []
          state.acceptedReports = []
        }),
        builder.addCase(getAdminReports.rejected, state => {
          state.userCasesLoading = false
        }),
        builder.addCase(getAdminReports.fulfilled, (state, { payload }) => {
          // console.log('getAdminReports.fulfilled')
          const data = { ...payload }
          state.userCasesLoading = false
          state.assignedToMeForTriage = data[triageLevelStates.TRANSFERED_FOR_ASSIGNMENT]
          state.immediateAttention = mergeArrays(
              state.immediateAttention,
              data[triageLevelStates.IMMEDIATE_ATTENTION_REQUIRED] || [])
          state.additionalInformationRequired = mergeArrays(
              state.additionalInformationRequired,
              data[triageLevelStates.ADDITIONAL_INFORMATION_REQUIRED] || [])
          state.addressedAndResolved = data[triageLevelStates.ADDRESSED_AND_RESOLVED]
          state.acceptedReports = data[triageLevelStates.ACCEPTED_AND_ASSIGNED]
        }),
        builder.addCase(getAdminReports.pending, state => {
          // console.log('getAdminReports.pending')
          state.userCasesLoading = true
          state.assignedToMeForTriage = []
          state.immediateAttention = []
          state.additionalInformationRequired = []
          state.addressedAndResolved = []
          state.acceptedReports = []
        }),
        builder.addCase(getUsersReports.rejected, state => {
          state.userCasesLoading = false
        }),
        
        
        builder.addCase(openCase.pending, state => {
          state.caseIsOpening = true
        }),
        builder.addCase(openCase.fulfilled, (state, { payload }) => {
          state.caseIsOpening = false
          state.openedCase = payload
        }),
        builder.addCase(openCase.rejected, state => {
          state.caseIsOpening = false
        }),
        builder.addCase(getReportAnswers.fulfilled, (state, { payload }) => {
          state.reportAnswers = payload
        }),
        builder.addCase(getCaseTypeQuestions.pending, state => {
          state.loading = true
          state.caseTypeQuestions = []
        }),
        builder.addCase(getCaseTypeQuestions.rejected, state => {
          state.loading = false
          state.caseTypeQuestions = []
        }),
        builder.addCase(getCaseTypeQuestions.fulfilled, (state, { payload }) => {
          if (state.loading) {
            if (!payload) {
              return state
            }
            const found = state.caseTypeQuestions?.find(item => item?.caseType === payload?.caseType)
            if (!found) {
              state.caseTypeQuestions.push(payload)
            }
            state.loading = false
          }
        }),
        builder.addCase(getArchivedReports.fulfilled, (state, { payload }) => {
          state.addressedAndResolvedLoading = false
          state.addressedAndResolved = payload
        }),
        
        builder.addCase(getArchivedReports.pending, (state, { payload }) => {
          state.addressedAndResolvedLoading = true
          state.addressedAndResolved = []
        }),
        builder.addCase(getArchivedReports.rejected, (state, { payload }) => {
          state.addressedAndResolvedLoading = false
          state.addressedAndResolved = []
        }),
        builder.addCase(getReportTeam.fulfilled, (state, { payload }) => {
          if (state.openedCase) {
            state.openedCase.teamMembers = payload
          }
        }),
        builder.addCase(getQuestionsForArrayOfTypes.pending, state => {
          state.loading = true
          // state.caseTypeQuestions = []
        }),
        builder.addCase(getQuestionsForArrayOfTypes.rejected, state => {
          state.loading = false
          // state.caseTypeQuestions = []
        }),
        builder.addCase(getQuestionsForArrayOfTypes.fulfilled, (state, { payload }) => {
          if (state.loading) {
            if (!payload || state.caseTypeQuestions.length >= payload?.length) {
              return state
            }
            payload?.forEach(caseTypeQuestions => {
              state.caseTypeQuestions.push(caseTypeQuestions)
            })
            state.loading = false
          }
        })
  },
})

export const {
  deselectCase,
  removeSelectedCaseType,
  setInitialAnswers,
  updateAnswerOption,
  updateAnswerText,
  setInitialValueToAnswer,
  updateAnswerFiles,
  setDefaultAnswersState,
  resetNumberOfChanges,
  addNewVictimForm,
  removeVictimForm,
  setVictimValues,
  updateVictimAnswerOption,
  updateVictimAnswerText,
  setReporterValues,
  updateSelectedReportCaseTypes,
  clearCaseTypeQuestions
} = reportsSlice.actions

export default reportsSlice.reducer
